import {
  CreateParkingLotMutationVariables,
  DeleteParkingLotMutationVariables,
  UpdateParkingLotMutationVariables,
} from "@/types/gql/graphql";
import {
  QUERY_ADD_PARKING_LOT,
  QUERY_DELETE_PARKING_LOT,
  QUERY_PARKING_LOT,
  QUERY_UPDATE_PARKING_LOT,
} from "@/services/web/api-parking-lot.const";
import { hideGlobalLoading, showGlobalLoading } from "@/hooks/use-global-loading";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";

export const useQueryParkingLot = () => {
  const { currentCity } = useContext(CityContext);
  const [isResetData, setIsResetData] = useState<boolean>(false);

  const variables = useMemo(() => {
    return {
      cityId: currentCity?.id ?? "",
    };
  }, [currentCity]);

  const refetchQueriesConfig = useMemo(() => {
    return [{ query: QUERY_PARKING_LOT, variables }];
  }, [variables]);

  const { data, loading } = useTrackedQuery(QUERY_PARKING_LOT, {
    actionName: "QueryParkingLots",
    variables,
    skip: !currentCity?.id,
  });

  useEffect(() => {
    if (loading) {
      showGlobalLoading();
    } else {
      hideGlobalLoading();
    }
  }, [loading]);

  const parkingLots = useMemo(() => {
    return data?.parkingLots || [];
  }, [data]);

  const resetState = () => {
    setIsResetData(true);
  };

  const [createParkingLot] = useTrackedMutation(QUERY_ADD_PARKING_LOT, {
    actionName: "CreateParkingLot",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const [updateParkingLot] = useTrackedMutation(QUERY_UPDATE_PARKING_LOT, {
    actionName: "UpdateParkingLot",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const [removeParkingLot] = useTrackedMutation(QUERY_DELETE_PARKING_LOT, {
    actionName: "DeleteParkingLot",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: resetState,
  });

  const handleAddParkingLot = async (input: CreateParkingLotMutationVariables) => {
    const response = await createParkingLot({ variables: input });
    return response.data;
  };

  const handleUpdateParkingLot = async (input: UpdateParkingLotMutationVariables) => {
    const response = await updateParkingLot({ variables: input });
    return response.data;
  };

  const handleDeleteParkingLot = async (input: DeleteParkingLotMutationVariables) => {
    const response = await removeParkingLot({ variables: input });
    return response.data;
  };

  return {
    parkingLots,
    loading,
    currentCity,
    isResetData,
    setIsResetData,
    handleAddParkingLot,
    handleUpdateParkingLot,
    handleDeleteParkingLot,
  };
};
